// pattern: functional-core
import { nanoid } from "nanoid";

export const STAGES = [
	"dormant",
	"emerging",
	"budding",
	"blooming",
	"fruiting",
	"declining",
	"dead",
] as const;
export type Stage = (typeof STAGES)[number];

export interface Observation {
	id: string;
	observed_date: string; // YYYY-MM-DD
	plant_name: string;
	location_id: string;
	stage: Stage;
	notes: string | null;
	raw_input: string | null;
	created_at: number;
	updated_at: number;
}

export interface ObservationInput {
	observed_date: string;
	plant_name: string;
	location_id: string;
	stage: string;
	notes?: string;
	raw_input?: string;
}

export interface ObservationUpdate {
	observed_date?: string;
	plant_name?: string;
	location_id?: string;
	stage?: string;
	notes?: string;
}

interface ValidationResult {
	ok: true;
	value: ObservationInput;
}

interface ValidationError {
	ok: false;
	error: string;
}

function str(input: Record<string, unknown>, key: string, fallback = ""): string {
	return String(input[key] ?? fallback);
}

function optStr(input: Record<string, unknown>, key: string): string | undefined {
	return input[key] ? String(input[key]).trim() : undefined;
}

export function validateObservationInput(
	input: Record<string, unknown>,
): ValidationResult | ValidationError {
	const date = str(input, "observed_date");
	if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return { ok: false, error: "observed_date must be YYYY-MM-DD" };
	}

	const plantName = str(input, "plant_name").trim();
	if (!plantName) {
		return { ok: false, error: "plant_name is required" };
	}

	const locationId = str(input, "location_id").trim();
	if (!locationId) {
		return { ok: false, error: "location_id is required" };
	}

	const stage = str(input, "stage").trim();
	if (!STAGES.includes(stage as Stage)) {
		return { ok: false, error: `stage must be one of: ${STAGES.join(", ")}` };
	}

	return {
		ok: true,
		value: {
			observed_date: date,
			plant_name: plantName,
			location_id: locationId,
			stage,
			notes: optStr(input, "notes"),
			raw_input: optStr(input, "raw_input"),
		},
	};
}

export function createObservation(input: ObservationInput): Observation {
	const now = Date.now();
	return {
		id: nanoid(12),
		observed_date: input.observed_date,
		plant_name: input.plant_name,
		location_id: input.location_id,
		stage: input.stage as Stage,
		notes: input.notes ?? null,
		raw_input: input.raw_input ?? null,
		created_at: now,
		updated_at: now,
	};
}
